import React, { useState } from 'react';
import { Upload, Button, Card, Steps, Progress, Result, message, Radio, Space, Row, Col, Tag, Tooltip, Badge, Collapse, Input, Modal } from 'antd';
import {
  UploadOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  AudioOutlined,
  CloudDownloadOutlined,
  RobotOutlined,
  SoundOutlined,
  LoadingOutlined,
  CommentOutlined
} from '@ant-design/icons';
import axios from 'axios';
import 'antd/dist/reset.css';
import './App.css';

// 配置axios的baseURL指向线上API
axios.defaults.baseURL = 'https://cast.workflowhunt.com';

const { Dragger } = Upload;
const { Step } = Steps;
const { Panel } = Collapse;

function App() {
  const [file, setFile] = useState(null);
  const [outputType, setOutputType] = useState('full_podcast'); // full_podcast or script_first
  const [status, setStatus] = useState('idle'); // idle, generating, completed, error
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scriptContent, setScriptContent] = useState(null);
  const [editingScript, setEditingScript] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.docx,.txt',
    beforeUpload: (file) => {
      const isDocxOrTxt = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                         file.type === 'text/plain' ||
                         file.name.endsWith('.docx') ||
                         file.name.endsWith('.txt');

      if (!isDocxOrTxt) {
        message.error('只能上传 DOCX 或 TXT 文件!');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过 10MB!');
        return false;
      }

      setFile(file);
      return false;
    },
    onRemove: () => {
      setFile(null);
    },
    fileList: file ? [file] : [],
  };

  const handleGenerate = async () => {
    if (!file) {
      message.error('请先上传文件');
      return;
    }

    try {
      setUploading(true);
      setStatus('generating');

      const formData = new FormData();
      formData.append('file', file);

      // 根据输出类型决定请求参数
      if (outputType === 'full_podcast') {
        formData.append('output_type', 'both');
        const response = await axios.post('/api/generate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setResult(response.data);
        setStatus('completed');
        message.success('播客生成完成!');
      } else {
        // script_first 模式：只生成脚本
        formData.append('output_type', 'script_only');
        const response = await axios.post('/api/generate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setResult(response.data);

        // 获取脚本内容
        if (response.data.script_file) {
          try {
            const scriptUrl = `${response.data.script_file}`;
            const scriptResponse = await axios.get(scriptUrl);
            setScriptContent(scriptResponse.data);
            setEditingScript(JSON.parse(JSON.stringify(scriptResponse.data))); // 深拷贝用于编辑
          } catch (error) {
            console.error('获取脚本内容失败:', error);
            setScriptContent({ dialogue: "脚本内容加载失败", basic_info: {} });
          }
        }

        setStatus('completed');
        message.success('脚本生成完成!');
      }

    } catch (error) {
      console.error('生成失败:', error);
      setStatus('error');
      message.error('生成失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!editingScript) {
      message.error('没有可用的脚本数据');
      return;
    }

    try {
      setAudioGenerating(true);
      message.loading('正在生成音频...', 0);

      const response = await axios.post('/api/generate-audio-from-script', editingScript, {
        headers: { 'Content-Type': 'application/json' },
      });

      message.destroy();
      message.success('音频生成完成!');

      // 更新结果，添加音频文件信息
      setResult(prev => ({
        ...prev,
        audio_file: response.data.audio_file,
        task_id: response.data.task_id,
        duration: response.data.duration
      }));

    } catch (error) {
      message.destroy();
      console.error('音频生成失败:', error);
      message.error('音频生成失败，请重试');
    } finally {
      setAudioGenerating(false);
    }
  };

  const handleEditScript = () => {
    console.log('编辑脚本数据:', editingScript);
    console.log('是否为数组:', Array.isArray(editingScript));
    if (!Array.isArray(editingScript)) {
      console.log('dialogue字段:', editingScript?.dialogue);
    }
    setEditModalVisible(true);
  };

  const handleSaveScript = () => {
    setScriptContent(JSON.parse(JSON.stringify(editingScript)));
    setEditModalVisible(false);
    message.success('脚本已保存');
  };

  const handleDialogueTextChange = (index, newText) => {
    let updatedScript;
    if (Array.isArray(editingScript)) {
      // 数组格式
      updatedScript = [...editingScript];
      updatedScript[index].text = newText;
    } else {
      // 对象格式
      updatedScript = { ...editingScript };
      updatedScript.dialogue[index].text = newText;
    }
    setEditingScript(updatedScript);
  };

  const handleDownload = (type) => {
    if (!result) return;

    let url = '';
    let filename = '';

    switch (type) {
      case 'script':
        if (result.script_file) {
          url = `${result.script_file}`;
          filename = `podcast_${result.task_id}_script.json`;
        }
        break;
      case 'audio':
        if (result.audio_file) {
          url = `${result.audio_file}`;
          filename = `podcast_${result.task_id}.mp3`;
        }
        break;
      case 'subtitle':
        if (result.subtitle_file) {
          url = `${result.subtitle_file}`;
          filename = `podcast_${result.task_id}.srt`;
        }
        break;
    }

    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleReset = () => {
    setFile(null);
    setStatus('idle');
    setResult(null);
    setScriptContent(null);
  };

  const getCurrentStep = () => {
    if (status === 'idle') return file ? 1 : 0;
    if (status === 'generating') return 2;
    if (status === 'completed') return 3;
    return 0;
  };

  return (
    <div className="App">
      <div className="container">
        {/* Header */}
        <div className="header">
          <Badge count="AI" style={{ backgroundColor: '#52c41a' }}>
            <div className="logo">
              <AudioOutlined style={{ fontSize: '36px', color: 'white' }} />
            </div>
          </Badge>
          <h1>智能播客生成器</h1>
          <p>将复杂的裁判文书转换为生动易懂的播客对话，让法律知识传播更高效</p>
        </div>

        {/* Main Content */}
        <Card className="main-card">
          <Steps current={getCurrentStep()} className="steps">
            <Step title="上传文件" icon={<FileTextOutlined />} />
            <Step title="选择格式" icon={<SoundOutlined />} />
            <Step title="生成播客" icon={<RobotOutlined />} />
            <Step title="下载结果" icon={<CloudDownloadOutlined />} />
          </Steps>

          {status === 'idle' && (
            <div className="content">
              {/* Upload Area */}
              <div className="upload-section">
                <h3><FileTextOutlined /> 上传裁判文书</h3>
                <Dragger {...uploadProps} className="upload-area">
                  <p className="ant-upload-drag-icon">
                    {file ? (
                      <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                    ) : (
                      <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                    )}
                  </p>
                  <p className="ant-upload-text">
                    {file ? file.name : '点击或拖拽文件到此区域上传'}
                  </p>
                  <p className="ant-upload-hint">
                    支持 DOCX、TXT 格式，最大 10MB
                  </p>
                </Dragger>
                {file && (
                  <div className="file-info">
                    <CheckCircleOutlined /> 已选择: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              {/* Output Type Selection */}
              <div className="output-section">
                <h3><SoundOutlined /> 选择生成方式</h3>
                <Radio.Group value={outputType} onChange={(e) => setOutputType(e.target.value)}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Radio.Button value="full_podcast" className="output-option">
                        <div>
                          <div>🎧 完整播客包</div>
                          <Tag color="blue">一键生成音频 + 字幕</Tag>
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col xs={24} md={12}>
                      <Radio.Button value="script_first" className="output-option">
                        <div>
                          <div>✏️ 先脚本后音频</div>
                          <Tag color="purple">可编辑脚本内容</Tag>
                        </div>
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </div>

              {/* Generate Button */}
              <div className="generate-section">
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  onClick={handleGenerate}
                  disabled={!file}
                  loading={uploading}
                  className="generate-btn"
                >
                  {uploading ? '处理中...' : (outputType === 'full_podcast' ? '开始生成播客' : '生成新脚本')}
                </Button>
              </div>
            </div>
          )}

          {status === 'generating' && (
            <div className="generating">
              <LoadingOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '24px' }} />
              <h2>正在生成播客...</h2>
              <p>AI 正在分析案情并生成专业的播客内容，请稍候片刻</p>
              <div className="loading-indicator">
                <div className="loading-dot" />
                <span>智能处理中，请耐心等待</span>
              </div>
            </div>
          )}

          {status === 'completed' && result && (
            <div className="completed-section">
              {/* 成功状态头部 */}
              <Result
                status="success"
                title={
                  <h2 style={{ color: '#52c41a' }}>
                    {outputType === 'full_podcast' ? '播客生成完成！' : '脚本生成完成！'}
                  </h2>
                }
                subTitle={
                  <p>
                    成功生成 {result.dialogue_count || 6} 段对话
                    {result.duration && `，总时长 ${Math.round(result.duration)} 秒`}
                  </p>
                }
              />

              {/* 下载按钮区域 */}
              <div className="download-section">
                <Space size="large" wrap>
                  {/* 脚本优先模式的按钮 */}
                  {outputType === 'script_first' && editingScript && (
                    <>
                      <Button
                        type="primary"
                        icon={<CommentOutlined />}
                        onClick={handleEditScript}
                      >
                        编辑脚本
                      </Button>
                      <Button
                        type="primary"
                        icon={<AudioOutlined />}
                        onClick={handleGenerateAudio}
                        loading={audioGenerating}
                      >
                        生成配音
                      </Button>
                    </>
                  )}

                  {/* 完整播客包模式的下载按钮 */}
                  {outputType === 'full_podcast' && (
                    <>
                      {result.audio_file && (
                        <Tooltip title="下载 MP3 音频文件">
                          <Button type="primary" icon={<AudioOutlined />} onClick={() => handleDownload('audio')}>
                            下载音频
                          </Button>
                        </Tooltip>
                      )}
                      {result.subtitle_file && (
                        <Tooltip title="下载 SRT 字幕文件">
                          <Button icon={<FileTextOutlined />} onClick={() => handleDownload('subtitle')}>
                            下载字幕
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}

                  {/* 脚本优先模式下生成音频后的下载按钮 */}
                  {outputType === 'script_first' && result.audio_file && (
                    <Tooltip title="下载 MP3 音频文件">
                      <Button type="primary" icon={<AudioOutlined />} onClick={() => handleDownload('audio')}>
                        下载音频
                      </Button>
                    </Tooltip>
                  )}

                  <Button onClick={handleReset}>生成新的播客</Button>
                </Space>
              </div>

              {/* 脚本内容展示 */}
              {scriptContent && (
                <Card className="script-section">
                  <div className="script-header">
                    <CommentOutlined />
                    <h3>播客脚本内容</h3>
                  </div>
                  <Collapse className="script-collapse" items={[
                    {
                      key: 'dialogue',
                      label: '📋 对话脚本',
                      style: { fontSize: '16px', fontWeight: 600 },
                      children: (
                        <div className="script-content">
                          {Array.isArray(scriptContent.dialogue) ? (
                            scriptContent.dialogue.map((item, index) => (
                              <div key={index} className="dialogue-line">
                                <span className="speaker">{item.speaker}:</span>
                                <span className="text">{item.text}</span>
                                {item.emotion && (
                                  <span className="emotion">[{item.emotion}]</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <pre className="script-pre">
                              {typeof scriptContent === 'string'
                                ? scriptContent
                                : JSON.stringify(scriptContent, null, 2)
                              }
                            </pre>
                          )}
                        </div>
                      )
                    },
                    ...(scriptContent.basic_info ? [{
                      key: 'basic_info',
                      label: 'ℹ️ 案情基本信息',
                      style: { fontSize: '16px', fontWeight: 600 },
                      children: (
                        <div className="info-grid">
                          {Object.entries(scriptContent.basic_info).map(([key, value]) => (
                            <div key={key} className="info-item">
                              <strong>{key}:</strong>
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                            </div>
                          ))}
                        </div>
                      )
                    }] : []),
                    ...(scriptContent.fraud_methods && scriptContent.fraud_methods.length > 0 ? [{
                      key: 'fraud_methods',
                      label: '⚠️ 舞弊手段',
                      style: { fontSize: '16px', fontWeight: 600 },
                      children: (
                        <ul className="method-list">
                          {scriptContent.fraud_methods.map((item, index) => (
                            <li key={index}>
                              <strong>{item.method}</strong>
                              {item.details && <p>{item.details}</p>}
                              {item.key_factor && <small>关键因素: {item.key_factor}</small>}
                            </li>
                          ))}
                        </ul>
                      )
                    }] : []),
                    ...(scriptContent.risk_points && scriptContent.risk_points.length > 0 ? [{
                      key: 'risk_points',
                      label: '🎯 风险要点',
                      style: { fontSize: '16px', fontWeight: 600 },
                      children: (
                        <ul className="risk-list">
                          {scriptContent.risk_points.map((item, index) => (
                            <li key={index}>
                              <strong>{item.weakness}</strong>
                              {item.suggestion && <p>{item.suggestion}</p>}
                            </li>
                          ))}
                        </ul>
                      )
                    }] : [])
                  ]} />
                </Card>
              )}
            </div>
          )}

          {status === 'error' && (
            <Result
              status="error"
              title="生成失败"
              subTitle="请检查文件格式或稍后重试"
              extra={[
                <Button type="primary" onClick={handleReset}>
                  重新开始
                </Button>
              ]}
            />
          )}

          {/* 脚本编辑模态框 */}
          <Modal
            title="编辑脚本内容"
            open={editModalVisible}
            onOk={handleSaveScript}
            onCancel={() => setEditModalVisible(false)}
            width={800}
            okText="保存"
            cancelText="取消"
          >
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {editingScript && (
                Array.isArray(editingScript)
                  ? editingScript.map((item, index) => (
                      <Card key={index} size="small" style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#1890ff' }}>{item.speaker}:</strong>
                          {item.emotion && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>{item.emotion}</Tag>
                          )}
                        </div>
                        <Input.TextArea
                          value={item.text}
                          onChange={(e) => handleDialogueTextChange(index, e.target.value)}
                          rows={3}
                          placeholder="请输入对话内容..."
                        />
                      </Card>
                    ))
                  : editingScript.dialogue && editingScript.dialogue.map((item, index) => (
                      <Card key={index} size="small" style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#1890ff' }}>{item.speaker}:</strong>
                          {item.emotion && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>{item.emotion}</Tag>
                          )}
                        </div>
                        <Input.TextArea
                          value={item.text}
                          onChange={(e) => handleDialogueTextChange(index, e.target.value)}
                          rows={3}
                          placeholder="请输入对话内容..."
                        />
                      </Card>
                    ))
              )}
            </div>
          </Modal>
        </Card>

        {/* Features */}
        <Row gutter={[24, 24]} className="features">
          <Col xs={24} md={8}>
            <Card hoverable className="feature-card">
              <RobotOutlined className="feature-icon" />
              <h3>智能分析</h3>
              <p>AI 自动分析案情核心信息，提取关键舞弊手段，生成专业对话内容</p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card hoverable className="feature-card">
              <AudioOutlined className="feature-icon" />
              <h3>专业配音</h3>
              <p>高质量语音合成，男女声搭配，情感丰富，让播客内容更加生动</p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card hoverable className="feature-card">
              <CloudDownloadOutlined className="feature-icon" />
              <h3>多格式导出</h3>
              <p>支持 MP3 音频、SRT 字幕、JSON 脚本多种格式，满足不同使用需求</p>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default App;